import find from 'lodash.find';
import debounce from 'lodash.debounce';
import numeral from 'numeral';
import config, { isProd } from '../../config';
import { unlockNavigation } from '../../preventNavigation';
import { StaticPhone } from '../../core/types/phone';
import {
  distributeRemainder,
  navigateToExternal,
  withDefault,
  parseAmount,
  authenticatedWithDefault,
  getReadableStringFromHash,
  isEmpty,
  isEmptyArray
} from '../../core/util';
import { meetsMinimumInvestment, MIN_CONTRIBUTION_ERR_MSG } from './testers';
import { StaticAddress } from '../../core/types/address';
import { DataLoader } from '../../core/types/data-loader';
import { StateStoreOrVoid } from '../../core/types/application-state';
import { analyticsDataLayer } from '../../analytics/analytics-helper';
import {
  getFONASUAnalyticsAccountType,
  productTypesAnalytics
} from '../../analytics/util';

export const I_CLASS_MINIMUM = 500000;
export const secondaryCommitMessage = `This could take a minute, <b>please avoid clicking the back button, refreshing the page or
  closing the browser</b> until you see your order confirmation. A confirmation will also be sent to your email.`;

export enum PhoneNumberType {
  CELLULAR = 'MOBILE',
  DAYTIME = 'DAYTIME',
  EVENING = 'EVENING'
}

export const consumerTypes = {
  DEFAULT: 'NASU',
  FONASU: 'FRONT_OFFICE_NASU'
};

export const sourceTypes = {
  DEFAULT: 'WEBNASU',
  FONASU: 'FONASU'
};

export enum accountTypes {
  ROLLOVER = 'ROLLOVER_IRA',
  TRANSFER = 'TRANSFER_IRA',
  TRADITIONAL = 'TRADITIONAL_IRA',
  ROTH = 'ROTH_IRA',
  CONTRIBUTORY = 'CONTRIBUTORY_IRA',
  SCHRODINGER = 'SCHRODINGER',
  INDIVIDUAL = 'INDIVIDUAL',
  JOINT = 'JOINT_TENANT',
  UGMAUTMA = 'UGMA_UTMA',
  TRANSFER_INDIVIDUAL = 'TRANSFER_INDIVIDUAL',
  TRANSFER_JOINT = 'TRANSFER_JOINT',
  TRANSFER_UGMAUTMA = 'TRANSFER_UGMA_UTMA',
  TRUST = 'TRUST',
  TOD = 'TRANSFER_ON_DEATH',
  ALASKA = 'COLLEGE_SAVINGS_ALASKA',
  NATIONAL = 'COLLEGE_SAVINGS_NATIONAL',
  ROTH_ROLLOVER = 'ROTH_ROLLOVER_IRA'
}

export const getAccountFinderUrl = (): string =>
  `${config('returnUrlPrefix')}personal-investing/account-finder/index.html`;

export const redirectToAccountFinder = (): void => {
  const accountFinderUrl = getAccountFinderUrl();
  navigateToExternal(accountFinderUrl);
};

export const isTrust = (accountType = ''): boolean =>
  accountType === accountTypes.TRUST;

export const isContributory = (accountType = ''): boolean =>
  accountType === accountTypes.CONTRIBUTORY;

export const isRetirement = (accountType = ''): boolean => {
  if (
    [
      accountTypes.CONTRIBUTORY,
      accountTypes.ROLLOVER,
      accountTypes.TRANSFER,
      accountTypes.ROTH,
      accountTypes.TRADITIONAL,
      accountTypes.ROTH_ROLLOVER
    ].includes(accountType as accountTypes)
  )
    return true;
  return false;
};

export const isNonTransferRetirement = (accountType = ''): boolean => {
  if (
    [
      accountTypes.CONTRIBUTORY,
      accountTypes.ROLLOVER,
      accountTypes.ROTH,
      accountTypes.TRADITIONAL
    ].includes(accountType as accountTypes)
  )
    return true;
  return false;
};

export const isTaxable = (accountType: string): boolean => {
  if (
    accountType === accountTypes.INDIVIDUAL ||
    accountType === accountTypes.JOINT ||
    accountType === accountTypes.UGMAUTMA ||
    accountType === accountTypes.TRUST
  )
    return true;
  return false;
};

export const isNonTrustTaxableOrContributory = (accountType = ''): boolean => {
  if (
    accountType === accountTypes.INDIVIDUAL ||
    accountType === accountTypes.JOINT ||
    accountType === accountTypes.UGMAUTMA ||
    accountType === accountTypes.CONTRIBUTORY
  )
    return true;
  return false;
};

export enum rpsToAccountTypes {
  ROLLOVER = 'ROLLOVER',
  ROTH_ROLLOVER = 'ROTH_ROLLOVER',
  TRADITIONAL = 'TRADITIONAL',
  ROTH = 'ROTH'
}

export enum rpsToAccountTypesForDisplay {
  ROLLOVER = 'Rollover',
  ROTH_ROLLOVER = 'Roth Rollover',
  TRADITIONAL = 'Traditional',
  ROTH = 'Roth'
}

export const customerTypes = {
  MINOR: 'MINOR',
  RETAIL: 'RETAIL',
  UNKNOWN: 'UNKNOWN',
  JOINT_TENANT: 'JOINT_TENANT',
  TRUSTEE: 'TRUSTEE',
  BENEFICIARY: 'BENEFICIARY',
  SUCCESSOR: 'SUCCESSOR'
};

export const chargeTypes = {
  TRANSFER: 'TRANSFER',
  NEW: 'NEW',
  ROLLOVER: 'ROLLOVER'
};

export const fundingTypes = {
  CHECK: 'CHECK',
  FROM_EXISTING_ACCOUNT: 'FROM_EXISTING_ACCOUNT',
  MANUAL: 'MANUAL_ENTRY',
  LINK: 'LINK',
  EXISTING_BANK: 'EXISTING_BANK',
  EFT: 'EFT',
  EXTERNAL: 'EXTERNAL_PROVIDER',
  FUND_LATER: 'FUND_LATER'
};

export const investmentMethods = {
  BYOP: 'BYOP',
  RDF: 'RDF',
  RECOMMENDED: 'RECOMMENDED',
  MULTIPLE: 'MULTIPLE',
  EXISTING: 'EXISTING_ONLY'
};

export const applicationStatusTypes = {
  PUSH_TO_WEB: 'PUSH_TO_WEB',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED'
};

export const rolloverTypes = {
  DIRECT: 'DIRECT',
  INDIRECT: 'INDIRECT'
};

export const RPSCustodianValues = {
  PRODCUSTNAME: 'T Rowe Price (Internal Rollovers Only)',
  PRODCUSTID: 'a2D2G0000070LDuUAM',
  STAGECUSTNAME: 'T. Rowe Price',
  STAGECUSTID: 'a1s2g000000pcOOAAY'
};

export const BankFailureTypes = {
  SWITCHTOCHECK: 'switch_to_check',
  SWITCHFUNDINGTOCHECK: 'switch_funding_to_check'
};

export const citizenshipTypes = {
  US_CITIZEN: 'C',
  RESIDENT_ALIEN: 'R'
};

export const RETIREMENT_AGE = 65;

export const isTaxableTransfer = (
  accountType: accountTypes | string = '',
  chargeType = ''
): boolean => {
  if (
    [
      accountTypes.INDIVIDUAL,
      accountTypes.JOINT,
      accountTypes.UGMAUTMA
    ].includes(accountType as accountTypes) &&
    chargeType === chargeTypes.TRANSFER
  )
    return true;

  return false;
};

export const isTrustExistingFlow = (
  accountType: string,
  fundingType: string
): boolean => {
  if (
    accountType === accountTypes.TRUST &&
    fundingType === fundingTypes.FROM_EXISTING_ACCOUNT
  )
    return true;
  return false;
};

export const parsePhone = (
  phone: string,
  type: PhoneNumberType = PhoneNumberType.CELLULAR
): StaticPhone => {
  const phoneArray = phone.split(/[^0-9]+/).filter(Boolean);

  return {
    areaCode: phoneArray[0],
    exchange: phoneArray[1],
    lastFour: phoneArray[2],
    type
  };
};

export const setRJCustomerPhones = (
  profilePhones,
  customerPhones
): Array<StaticPhone> => {
  // if enabled customer is coming back through RJ profile
  // profile journey phone numbers take precedence if present
  // default to what phone numbers are on the customer's file
  if (profilePhones?.length) {
    return profilePhones.map(
      ({ phoneNumberType: type = '', phoneNumber = '' }) => ({
        ...parsePhone(phoneNumber),
        type
      })
    );
  }

  return customerPhones;
};

export const convertCustomerPhoneNumbers = (phoneNumbers): Array<any> => {
  const parseType = (type = ''): string =>
    ({
      EVENING: PhoneNumberType.EVENING,
      DAYTIME: PhoneNumberType.DAYTIME
    }[type] || PhoneNumberType.CELLULAR);

  if (phoneNumbers && phoneNumbers.length > 0) {
    const phones = phoneNumbers.map(phone => ({
      areaCode: phone.areaCode,
      exchange: phone.exchange,
      lastFour: phone.localNumber,
      type: parseType(phone.phoneNumberType?.toUpperCase())
    }));

    return phones;
  }
  // default to empty array, as empty strings will cause new-account-application saving to fail with 400 validation error
  return [];
};

/**
 * This function formats the default SSN value
 * @param {string} ssn - ssn to format
 * @returns {string}
 */
export const formatSsnForDisplay = (ssn: string, isSSN = true): string => {
  return isSSN
    ? ssn && `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`
    : ssn && `${ssn.slice(0, 2)}-${ssn.slice(2, 9)}`;
};

export const formatAmountForDisplay = (amount): string =>
  parseFloat(amount) ? `$${numeral(amount).format('0,0[.]00')}` : '0';

/**
 * This function rounds the given number upto two decimal places
 * if the provided number is a decimal
 * @param {number} num - number to round
 * @returns {number}
 */
export const toHundreds = (num = 0): number =>
  Math.round((num + Number.EPSILON) * 100) / 100;

const agreementTypesForDisplay = {
  INDIVIDUAL: 'Individual',
  JOINT_TENANT: 'Joint Owner',
  UGMA_UTMA: 'UGMA/UTMA',
  TRUST: 'Trust',
  MINOR: 'UGMA/UTMA',
  TRANSFER_ON_DEATH: 'Transfer On Death'
};

export const formatAgreementTypeForDisplay = (type): string =>
  agreementTypesForDisplay[type];

export const setTaxIDType = (citizenship = ''): string =>
  ['NON_RESIDENT_ALIEN', 'RESIDENT_ALIEN'].includes(citizenship)
    ? 'TIN'
    : 'SSN';

const PHONE_LIMIT = 250000;
const PHONE_FUND_LIMIT = 500000;

export const phoneNumbers = {
  Investment: { lo: '1-855-681-8299', hi: '1-855-733-4779' },
  PICI: { lo: '1-855-405-5745', hi: '1-855-405-6470' },
  Funding: { lo: '1-855-405-5746', hi: '1-855-405-6471' },
  Review: { lo: '1-855-749-7971', hi: '1-855-749-7968' },
  mismatchInformation: { lo: '1-800-638-5660', hi: '1-800-638-5660' },
  mismatchNationalInformation: { lo: '1-866-415-5171', hi: '1-866-415-5171' },
  mismatchAlaskaInformation: { lo: '1-866-288-7476', hi: '1-866-288-7476' },
  rolloverConcierge: { lo: '1-866-294-5526', hi: '1-866-294-5526' },
  rolloverCallAssistancePhone: { lo: '1-855-405-5747', hi: '1-855-405-5747' },
  default: { lo: '1-855-793-6611', hi: '1-855-733-4770' },
  fundContributory: { lo: '1-855-681-8293', hi: '1-855-681-8293' },
  fundTaxable: { lo: '1-855-654-5326', hi: '1-855-654-5308' },
  contributoryPICI: { lo: '1-855-749-7941', hi: '1-855-749-7941' },
  contributoryReview: { lo: '1-855-749-7967', hi: '1-855-749-7971' },
  taxableBasicPI: { lo: '1-855-654-5321', hi: '1-855-654-5321' },
  taxableBYOP: { lo: '1-855-654-5324', hi: '1-855-654-5272' },
  taxablePICI: { lo: '1-855-530-0776', hi: '1-855-654-5322' },
  taxableBeneficiary: { lo: '1-855-530-0712', hi: '1-855-654-5320' },
  taxableCredentials: { lo: '1-855-530-0776', hi: '1-855-654-5322' },
  taxableReview: { lo: '1-855-530-0823', hi: '1-855-654-5325' }
};

type GetContactTextFn = (number) => string;

export const contactTextFn = (phone = 'default'): GetContactTextFn => (
  amt
): string => phoneNumbers[phone][amt >= PHONE_LIMIT ? 'hi' : 'lo'];

export const disclaimerPhoneTextFn = (phone = 'default'): GetContactTextFn => (
  amt
): string => phoneNumbers[phone][amt >= PHONE_FUND_LIMIT ? 'hi' : 'lo'];

export const redirectToCLX = (applicationID: string): any => {
  const url = `${config(
    'clxUrl'
  )}/usis/login/?pdurl=/clx-tk/token-exchange/redirect/nasu?sdurl=${config(
    'returnUrlPrefix'
  )}personal-investing/tools/nasu/return-journey/welcome?applicationID=${applicationID}`;

  navigateToExternal(url);
};

export const redirectToEnablement = (applicationID: string): any => {
  const url = `${config(
    'returnUrlPrefix'
  )}usis/login/enablement?pdurl=/clx-tk/token-exchange/redirect/nasu?sdurl=${config(
    'returnUrlPrefix'
  )}personal-investing/tools/nasu/return-journey/welcome?applicationID=${applicationID}`;

  return navigateToExternal(url);
};

export const logout = (): void => {
  unlockNavigation();
  window.location.href = `${config('clxUrl')}/usis/login/logout?sdurl=${config(
    'clxUrl'
  )}/usis/my-accounts/post-logout`;
};

export const navigateToLogin = (): void =>
  navigateToExternal(
    `${config(
      'clxUrl'
    )}/usis/login/?pdurl=/clx-tk/token-exchange/redirect/nasu?sdurl=${
      window.location.href
    }`
  );

export const setAccountAccessUrl = (hasRetailEntitlement: boolean): string => {
  // determine url path based on if logged in user has retail entitlement
  // entitlement is necessary to view Account Access
  const urlToPushTo = hasRetailEntitlement
    ? `${config('clxUrl')}/usis/client-portal/dashboard`
    : `${config('clxUrl')}/usis/login/logout?sdurl=${config(
        'clxUrl'
      )}/usis/client-portal/dashboard`;

  return urlToPushTo;
};

// Function to replace part of a window.location URL
const replaceURLString = (oldURLString, newURLString): string => {
  let url = window.location.toString();
  url = url.replace(oldURLString, newURLString);
  return url;
};

export const navigateToProfileAfterEnablement = (accountType): void => {
  let redirectURLAfterEnablement;
  if (accountType === accountTypes.TRUST) {
    redirectURLAfterEnablement = replaceURLString(
      '/trustee/information',
      '/profile'
    );
    return navigateToExternal(
      `${config(
        'clxUrl'
      )}/usis/login/enablement?pdurl=/clx-tk/token-exchange/redirect/nasu?sdurl=${redirectURLAfterEnablement}`
    );
  }
  redirectURLAfterEnablement = replaceURLString('/information', '/profile');

  return navigateToExternal(
    `${config(
      'clxUrl'
    )}/usis/login/enablement?pdurl=/clx-tk/token-exchange/redirect/nasu?sdurl=${redirectURLAfterEnablement}`
  );
};

export const navigateToProfileAfterLogin = (accountType): void => {
  let redirectURLAfterLogin;
  if (accountType === accountTypes.TRUST) {
    redirectURLAfterLogin = replaceURLString(
      '/trustee/information',
      '/profile'
    );
    return navigateToExternal(
      `${config(
        'clxUrl'
      )}/usis/login/?pdurl=/clx-tk/token-exchange/redirect/nasu?sdurl=${redirectURLAfterLogin}`
    );
  }
  redirectURLAfterLogin = replaceURLString('/information', '/profile');

  return navigateToExternal(
    `${config(
      'clxUrl'
    )}/usis/login/?pdurl=/clx-tk/token-exchange/redirect/nasu?sdurl=${redirectURLAfterLogin}`
  );
};

export const getOriginalResidentialAddress = (
  residentialAddressValidation
): any => {
  return (
    residentialAddressValidation?.originalAddress ||
    residentialAddressValidation
  );
};

export const getCorrectedResidentialAddress = (
  residentialAddressValidation
): any => {
  return withDefault(residentialAddressValidation?.correctedAddress, '');
};

export const getOriginalMailingAddress = (mailingAddressValidation): any => {
  return mailingAddressValidation?.originalAddress || mailingAddressValidation;
};

export const getCorrectedMailingAddress = (mailingAddressValidation): any => {
  return mailingAddressValidation?.correctedAddress;
};

export const mapAddressType = (address): StaticAddress => ({
  street1: withDefault(address?.street1, ''),
  street2: withDefault(address?.street2, ''),
  street3: withDefault(address?.street3, ''),
  state: withDefault(address?.state, ''),
  zip: withDefault(address?.zip, ''),
  zip4: withDefault(address?.zip4, ''),
  city: withDefault(address?.city, '')
});

// @TODO [2020-06-30] : choover find a less hacky solution to debounce
// this fixes the issue with next being called multiple times
// but the correct fix is to figure out why component is being unmounted and remounted
export const runDebounced = debounce((fn, ...args) => fn(...args), 100, {
  leading: true,
  trailing: false
});

export const sumOf = (amounts: any[]): any => {
  // convert all values passed to numbers, so they are correctly added together
  const amountsNumberArray = amounts.map(a => Number(a));
  return amountsNumberArray.reduce((accum, currVal) => accum + currVal);
};

// value must be a String type to perform slice
// slice string amount from start, up to the number of places after the decimal
// this limits the amount to two decimal places, without rounding up
export const formatTwoDecimalAmountNoRounding = (val: any): string => {
  const stringVal = val.toString();
  return stringVal.includes('.')
    ? stringVal.slice(0, stringVal.indexOf('.') + 3)
    : stringVal;
};

export const toThousands = (num = 0): number => {
  const stringNum = num.toString();
  // round to the nearest thousandth if number is a decimal
  // otherwise return whole number percent
  return stringNum?.includes('.')
    ? parseFloat((((num + Number.EPSILON) * 100) / 100).toFixed(3))
    : Math.round((num + Number.EPSILON) * 100) / 100;
};

/* eslint-disable complexity */
export const addCommas = (input: string | number): string => {
  // this function is complex because regex lookarounds are not supported in Safari :(
  if (!input) return '';

  const str = input.toString();
  // first regex: remove commas
  // second regex: remove non-digit character at end of string
  const cleanStr = str.replace(/,/g, '').replace(/\D$/g, '');
  const array = Array.from(cleanStr);

  // add 0 on end if cents section only has 1 digit (ex: 1,400.1)
  if (array.indexOf('.') > -1 && !array[array.indexOf('.') + 2]) {
    array.push('0');
  }

  // find where to put first comma (depends on if cents are included)
  const startingCommaPosition = array.find(element => element === '.') ? 6 : 3;

  // add commas
  for (let i = startingCommaPosition; i < array.length; i += 4) {
    array.splice(array.length - i, 0, ',');
  }

  return array.join('');
};
/* eslint-enable complexity */

export const filterExistingInvestmentsFromNew = (
  existingAccounts: Array<any> = [],
  chosenInvestments: Array<any> = []
): Array<any> => {
  // capture existing funds tickers
  const existingFundsTickers =
    existingAccounts?.map(account => account?.investmentVehicle?.ticker) || [];
  // match existing tickers to chosen investments to filter existing funds out
  const filteredNewInvestments = chosenInvestments.filter(
    item => !existingFundsTickers.includes(item.ticker)
  );

  return filteredNewInvestments;
};

export const filterNewInvestmentsFromExisting = (
  existingAccounts: Array<any> = [],
  chosenInvestments: Array<any> = []
): Array<any> => {
  // capture existing funds tickers
  const existingFundsTickers =
    existingAccounts?.map(account => account?.investmentVehicle?.ticker) || [];
  // match existing tickers to chosen investments to filter existing funds out
  const filteredExistingInvestments = chosenInvestments.filter(item =>
    existingFundsTickers.includes(item?.ticker)
  );

  return filteredExistingInvestments;
};

export const formatAccountNumber = (accountNumber): any => {
  return accountNumber?.replace(/-/g, '');
};

export const formatFunds = (funds: any[]): any[] => {
  if (!funds || !funds.length) return [];
  const formattedfunds = funds.map(fund => ({
    name: fund.name,
    trpCode: fund.trpCode,
    ticker: fund.ticker,
    amount: withDefault(fund.amount, fund.fundingDetails?.fundingAmount),
    percent: withDefault(
      fund.percent,
      fund.fundingDetails?.fundingAmountPercentage
    ),
    unlockedFund: fund.unlockedFund
  }));
  return formattedfunds;
};

export const formatFundLaterFunds = (funds: any[]): any[] => {
  if (!funds || !funds.length) return [];
  const formattedfunds = funds.map(fund => ({
    name: fund.name,
    trpCode: fund.trpCode,
    ticker: fund.ticker,
    amount: 0,
    percent: 0,
    unlockedFund: fund.unlockedFund
  }));
  return formattedfunds;
};

export const autoSplitPercents = (funds, totalAmount): any => {
  const percent = 100 / funds.length;
  const percents = new Array(funds.length).fill(percent);
  const distributedPercents = distributeRemainder(percents, 100);
  return funds.map((f, i) => ({
    ticker: f.ticker,
    trpCode: f.trpCode,
    fundingDetails: {
      fundingAmount: parseAmount((distributedPercents[i] / 100) * totalAmount),
      fundingAmountPercentage: distributedPercents[i]
    }
  }));
};

export const allocateFunds = (chosenFunds): any => {
  return chosenFunds.map(f => ({
    ticker: f.ticker,
    trpCode: f.trpCode,
    fundingDetails: {
      fundingAmount: parseAmount(f.amount),
      fundingAmountPercentage: f.percent
    }
  }));
};

export const getCusip = (idx, cusipsArr = []): any => {
  return cusipsArr.find((_, index) => index === idx);
};

export const provideDefault = (value, formPropsField): any =>
  value || formPropsField?.defaultValue;

export const validateRPSData = (amount, plan): boolean => {
  if (!amount || !plan) {
    return false;
  }
  if (meetsMinimumInvestment(amount) === MIN_CONTRIBUTION_ERR_MSG) {
    return false;
  }
  if (plan.length < 1) {
    return false;
  }
  return true;
};

export const createRPSRolloverData = (params): any => {
  const { investmentAmt, plan } = params;

  if (!validateRPSData(investmentAmt, plan)) return {};

  return {
    rpsDirectRollover: true,
    rolloverType: rolloverTypes.DIRECT,
    amount: Number(investmentAmt),
    formerEmployerName: `internal-${plan}`,
    app: {
      custodian: {
        custodianName: isProd()
          ? RPSCustodianValues.PRODCUSTNAME
          : RPSCustodianValues.STAGECUSTNAME,
        custodianID: isProd()
          ? RPSCustodianValues.PRODCUSTID
          : RPSCustodianValues.STAGECUSTID
      },
      existingAccounts: []
    }
  };
};

export const decideFundMinimumProperty = (
  accountType,
  meetsAccountMinimumOpenIRA,
  meetsInvestmentAmount
): any =>
  accountType && accountType.slice(-4) === '_IRA'
    ? meetsAccountMinimumOpenIRA
    : meetsInvestmentAmount;

export const changeMinimumInvestment = (funds, minimum): any => {
  if (!funds) return [];
  if (!minimum) return funds;
  return funds.map(f => {
    const newFund = f;
    newFund.minimumInvestmentAmount = minimum;
    return newFund;
  });
};

// following parse information function is to handle defaulting
// customer information when input values are no longer accessible
// i.e. in the case of a modal event
export const parseCustomerInformation = (inputs, customer, enabled): any => ({
  name: {
    firstName: withDefault(inputs.firstName, customer?.name?.firstName),
    middleName:
      withDefault(inputs.middleName, customer?.name?.middleName) || '',
    lastName: withDefault(inputs.lastName, customer?.name?.lastName)
  },
  taxID: enabled
    ? customer.taxID
    : withDefault(inputs.ssn?.replace(/[^*0-9]/g, ''), customer?.taxID),
  dob: withDefault(inputs.dob, customer?.dob) || '',
  taxIDType: withDefault(setTaxIDType(inputs.citizenship), customer?.taxIDType),
  citizenship: withDefault(inputs.citizenship, customer?.citizenship) || ''
});

/* eslint-disable */
export const setCustomerValidationPayload = (
  firstName,
  lastName,
  middleName,
  ssn,
  dob,
  customer
): any => ({
  firstName: withDefault(firstName, customer?.name?.firstName),
  lastName: withDefault(lastName, customer?.name?.lastName),
  middleName: withDefault(middleName, customer?.name?.middleName),
  taxID: withDefault(ssn, customer?.taxID),
  dob: withDefault(dob, customer?.dob)
});

export const setJointOwnersValidationPayload = (people, jointOwners): any => {
  return people.map((jo, index) => ({
    firstName: withDefault(
      jo.jointOwnerFirstName,
      jointOwners?.[index]?.name?.firstName
    ),
    lastName: withDefault(
      jo.jointOwnerLastName,
      jointOwners?.[index]?.name?.lastName
    ),
    taxID: withDefault(jo.jointOwnerSsn, jointOwners?.[index]?.taxID),
    dob: withDefault(jo.jointOwnerDob, jointOwners?.[index]?.dob)
  }));
};
/* eslint-enable */

export const removeSlash = (URL): string => {
  return URL.slice(-1) === '/' ? URL.slice(0, -1) : URL;
};

export const cleanContributoryInvestments = (investments): any => {
  return investments.map(i => {
    return {
      ticker: i?.ticker,
      trpCode: i?.trpCode,
      fundingDetails: [
        {
          fundingAmount: 0,
          contributionYearType: 'CURRENT_YEAR'
        }
      ]
    };
  });
};

export const extractPhoneNumberwithType = (phones, PhoneType): string => {
  if (phones && phones.length > 0) {
    return phones
      .map(item => {
        const { areaCode, exchange, lastFour, type } = item;
        if (type === PhoneType) {
          return [areaCode, exchange, lastFour].filter(Boolean).join('-');
        }
        return '';
      })
      .filter(Boolean)
      .join('');
  }
  return '';
};

export const getAppSourceType = (consumerName): string => {
  if (consumerName === consumerTypes.FONASU) {
    return sourceTypes.FONASU;
  }
  return sourceTypes.DEFAULT;
};

export const getAccountCategory = (accounType): string =>
  ({
    INDIVIDUAL: 'taxable',
    JOINT_TENANT: 'taxable',
    UGMA_UTMA: 'taxable',
    CONTRIBUTORY_IRA: 'contributory_ira',
    BROKERAGE: 'brokerage',
    COLLEGE_SAVINGS: 'college_savings'
  }[accounType] || 'contributory_ira');

export const setDefaultedProfileData = (inputs, store): any => ({
  dob: withDefault(inputs?.dob, store?.app?.customer?.dob),
  primaryEmailAddress: withDefault(
    inputs?.primaryEmailAddress,
    store?.app?.customer?.primaryEmailAddress
  ),
  firstName: withDefault(
    inputs?.firstName,
    store?.app?.customer?.name?.firstName
  ),
  lastName: withDefault(inputs?.lastName, store?.app?.customer?.name?.lastName)
});

export const findRetirementIncomeFund = (
  newInvestmentTicker,
  chosenInvestments
): any => {
  return find(chosenInvestments, choosenInvObj => {
    return (
      choosenInvObj.ticker === newInvestmentTicker &&
      choosenInvObj.retirementIncome
    );
  });
};

export const updateRIWithholding = (
  newInvestments,
  taxWithholdingOption,
  taxWithholdingPercent,
  chosenInvestments
): any => {
  // set percentage based on tax withholding option chosen; 0 for "NO"
  const federalWithholdingPercentForDividends =
    taxWithholdingOption?.toUpperCase() === 'YES'
      ? parseInt(
          taxWithholdingPercent,
          10
        ) /* make sure percentage is an int type */
      : 0;
  // update Retirement Income investment to include withholding percent
  const finalizedNewInvestments = newInvestments?.map(inv => {
    // locate Retirement Income investment to update with percentage
    if (findRetirementIncomeFund(inv.ticker, chosenInvestments)) {
      return {
        ...inv,
        federalWithholdingPercentForDividends
      };
    }

    // return any other investments "as is" if not Retirement Income fund
    return inv;
  });

  return finalizedNewInvestments;
};

/* eslint-disable complexity */
// [TODO] @Abhinav: remove the below 3 functions
// once we migrate to using beacon form fields
const addCommasForAmountFormatting = (str): string => {
  return str.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const formatDate = (str, max): string => {
  if (str.charAt(0) !== '0' || str === '00') {
    const num = Number(str);
    if (num <= 0 || num > max) {
      return '1';
    }
    if (str.length === 1 && num > Number(max.toString().charAt(0))) {
      return `0${str}`;
    }
  }
  return str;
};
/* eslint-enable complexity */

const Formatters = {
  LOCALE_STRING: (val): string =>
    Number(val.replace(/[^0-9.]/g, '')).toLocaleString('en'),
  DECIMAL_CURRENCY: (val): string => {
    const res = val.replace(/[^\d|.]/g, '');
    const [leadZeroWhole, decimal] = res.split(/\.(?=.)/);
    const whole = leadZeroWhole.replace(/^0+/, '');
    return addCommasForAmountFormatting(
      decimal
        ? `${whole}.${decimal.replace(/[^\d]/g, '').slice(0, 2)}`
        : whole || '0'
    );
  },
  PERCENT: (val): string =>
    val.replace(/[^\d]/g, '').replace(/\d\d\d+/g, '100'),
  SSN: (val): string =>
    val
      .replace(/\D/g, '')
      .substr(0, 9)
      .replace(/(\d{1,3})(\d{1,2})?(\d{1,4})?/g, (txt, f, s, t) => {
        if (t) {
          return `${f}-${s}-${t}`;
        }
        if (s) {
          return `${f}-${s}`;
        }
        if (f) {
          return `${f}`;
        }

        return '';
      }),
  DATE: (val): string =>
    val
      .replace(/\D/g, '')
      .substr(0, 8)
      .replace(/(\d{1,2})(\d{1,2})?(\d{1,4})?/g, (txt, f, s, t) => {
        if (t) {
          return `${f}/${s}/${t}`;
        }
        if (s) {
          return `${f}/${formatDate(s, 31)}`;
        }
        if (f) {
          return `${formatDate(f, 12)}`;
        }

        return '';
      }),
  EIN: (val): string =>
    val
      .replace(/\D/g, '')
      .substr(0, 9)
      .replace(/(\d{1,2})?(\d{1,7})?/g, (txt, f, t) => {
        if (t) {
          return `${f}-${t}`;
        }
        if (f) {
          return `${f}`;
        }

        return '';
      }),
  PHONE: (val): string =>
    val
      .replace(/\D/g, '')
      .substr(0, 10)
      .replace(/(\d{1,3})(\d{1,3})?(\d{1,4})?/g, (txt, f, s, t) => {
        if (t) {
          return `(${f}) ${s}-${t}`;
        }
        if (s) {
          return `(${f}) ${s}`;
        }
        if (f) {
          return `${f}`;
        }

        return '';
      })
};

export const handleSpecialInputFormat = (
  evt,
  formatType,
  setInputValue,
  uniqueKey
): string => {
  const { target } = evt;
  const formattedValue = Formatters[formatType](target.value);
  return setInputValue(prevValues => ({
    ...prevValues,
    [uniqueKey]: formattedValue
  }));
};

export const handleInputFormat = (evt, formatType, setInputValue): string => {
  const { target } = evt || {};
  const formattedValue = Formatters[formatType](target?.value);
  return setInputValue(formattedValue);
};

export const createFormURL = (blob: any): string => {
  try {
    return URL.createObjectURL(blob);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }
  // By default return empty string
  return '';
};

export const getCustomerMailingAddress = (addresses: any[] = []): any => {
  if (addresses?.length) {
    const mailingAddress = find(
      addresses,
      address => address.addressType === 'secondary'
    );
    return mapAddressType(mailingAddress);
  }
  return {};
};

const convertCitizenshipValue = (serviceCitizenshipType): string => {
  const type = {
    [citizenshipTypes.US_CITIZEN]: 'US_CITIZEN',
    [citizenshipTypes.RESIDENT_ALIEN]: 'RESIDENT_ALIEN'
  }[serviceCitizenshipType];
  return type;
};

export const parseCustomerData = (
  appStateStore,
  customerDataFromService,
  accountType
): any => {
  const {
    firstName,
    middleName = '',
    lastName,
    primaryEmailAddress,
    dateOfBirthString: dob,
    taxID,
    citizenshipType,
    primaryAddress,
    phoneNumbers: customerPhoneNumbers,
    addresses
  } = customerDataFromService;

  // customer service returns the last four as localNumber but application requires it as lastFour
  const modifiedPhoneNumbers = convertCustomerPhoneNumbers(
    customerPhoneNumbers
  );

  // check if trust account type and parse data for trustee vs customer
  if (isTrust(accountType)) {
    const trustee = {
      name: {
        firstName: authenticatedWithDefault(
          firstName,
          appStateStore?.app?.trustee?.name?.firstName,
          ''
        ),
        middleName: authenticatedWithDefault(
          middleName,
          appStateStore?.app?.trustee?.name?.middleName,
          ''
        ),
        lastName: authenticatedWithDefault(
          lastName,
          appStateStore?.app?.trustee?.name?.lastName,
          ''
        )
      },
      primaryEmailAddress: authenticatedWithDefault(
        primaryEmailAddress,
        appStateStore?.app?.trustee?.primaryEmailAddress,
        ''
      ),
      dob,
      taxID,
      citizenship: convertCitizenshipValue(citizenshipType),
      residentialAddress: mapAddressType(primaryAddress),
      phones: modifiedPhoneNumbers,
      mailingAddress: withDefault(getCustomerMailingAddress(addresses), {})
    };

    return { trustee };
  }

  const customer = {
    name: {
      firstName: authenticatedWithDefault(
        firstName,
        appStateStore.app?.customer?.name?.firstName,
        ''
      ),
      middleName: authenticatedWithDefault(
        middleName,
        appStateStore.app?.customer?.name?.middleName,
        ''
      ),
      lastName: authenticatedWithDefault(
        lastName,
        appStateStore.app?.customer?.name?.lastName,
        ''
      )
    },
    primaryEmailAddress: authenticatedWithDefault(
      primaryEmailAddress,
      appStateStore.app?.customer?.primaryEmailAddress,
      ''
    ),
    dob: authenticatedWithDefault(dob, appStateStore.app?.customer?.dob, ''),
    taxID,
    citizenship: convertCitizenshipValue(citizenshipType),
    residentialAddress: mapAddressType(primaryAddress),
    phones: modifiedPhoneNumbers,
    mailingAddress: withDefault(getCustomerMailingAddress(addresses), {})
  };

  return { customer };
};

export const isRetirementIncomeFundPresent = (funds: any[]): boolean =>
  funds?.some(({ retirementIncome }) => retirementIncome === true);

/**
 * This function returns the mailingAddressDifferent flag
 * specifically for forced login and RJ scenairos
 * @param {boolean} enabled - user enablement status
 * @param {object} store - store data
 * @param {object} profile - profile data
 * @returns {boolean}
 */
export const getMailingAddressDifferentFlag = (
  enabled,
  store,
  profile
): boolean => {
  if (enabled) {
    return withDefault(store?.mailingAddressDifferent, false);
  }
  return withDefault(profile?.residentialAddressDifferentFromMailing, false);
};

export const appendReadableStepString = (progressStepText = ''): string => {
  if (isEmpty(progressStepText)) return '';
  const progressStepHash = progressStepText.split(',').pop();
  const readableScreenStep = getReadableStringFromHash(progressStepHash);
  return `${progressStepText}:${readableScreenStep}`;
};

export const removeAppendedReadableStepString = (
  progressStepText = ''
): string => {
  // check if string is empty or delimiter is not present; bailout
  if (isEmpty(progressStepText) || !progressStepText.includes(':')) {
    return progressStepText;
  }
  // remove screen readable string from progress step list by colon delimiter
  const readableString = progressStepText.substring(
    progressStepText.indexOf(':')
  );
  const cleanedProgressSteps = progressStepText.replace(readableString, '');
  return cleanedProgressSteps;
};

// US582887 Mutual Fund Events
// capture account number(s) after submission for final profile svc (RJ) update
export const parseAccountNumbers = (
  loadedDataAfterSubmission: any
): Array<any> => {
  if (!isEmptyArray(loadedDataAfterSubmission?.accounts)) {
    const accountNumbers = loadedDataAfterSubmission.accounts.map(account => ({
      accountNumber: account?.accountNumber
    }));
    return accountNumbers;
  }

  return [];
};

// Check if flow is a non-funded one
// update with additional account type checks as non-funded is built out
export const isNonFundedFlow = (accountType = ''): boolean =>
  isNonTrustTaxableOrContributory(accountType);

//  get correct AgreementTypes based on account type by default except for
//  tranfer flow get it based off of toIRAAccountType and for contributory flow
//  get it based off of iraAccountType
export const getAgreementType = (savedApplication): string => {
  let agreementTypes;

  if (savedApplication?.accountType === accountTypes.CONTRIBUTORY) {
    agreementTypes = accountTypes[`${savedApplication?.app?.iraAccountType}`];
  } else if (savedApplication?.accountType === accountTypes.TRANSFER) {
    agreementTypes = accountTypes[`${savedApplication?.app?.toIRAAccountType}`];
  } else {
    agreementTypes = savedApplication?.accountType;
  }

  return agreementTypes;
};

export const updateFONASUAnalytics: DataLoader = async ({
  store
}): Promise<StateStoreOrVoid> => {
  analyticsDataLayer({
    accountType: getFONASUAnalyticsAccountType(store),
    productType: productTypesAnalytics.MUTUAL_FUND,
    sourceType: sourceTypes.FONASU
  });
};
